import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-typed-form';
import { FieldTextRequired, FieldSelect } from '../../form';
import ConfirmModal from '../../components/ConfirmModal';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { Api, useApiFormSubmit, useApiCallable } from '../../api';
import { TrashSimple } from '@phosphor-icons/react';

type Props = {
  existing?: Api.Tag.V | null;
};

export default function ContentTagForm({ existing }: Props) {
  const navigate = useNavigate();
  const callApi = useApiCallable();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const initialValues = {
    internalName: existing?.internalName || '',
    category: existing?.category?.['@id'] || '',
    userFacingName: existing?.userFacingName || '',
  };

  const form = useForm<{
    internalName: string;
    category: string;
    userFacingName: string;
  }>({
    defaultValues: initialValues,
    onSubmit: useApiFormSubmit(
      existing ? 'PUT' : 'POST',
      existing ? `/tags/${existing.id}` : '/tags',
      {
        onSuccess: () => {
          toast.success(
            existing ? 'Content tag updated' : 'Content tag created',
          );
          navigate('/library/content-tags');
        },
      },
    ),
  });

  const categoriesApi = Api.TagCategory.useReadCollectionAll();
  const categories = categoriesApi.data?.member ?? [];

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: category['@id'],
  }));

  return (
    <Stack gap={3}>
      <Box maxWidth={700}>
        <FieldTextRequired
          field={form.getField('internalName')}
          label="Name"
          required
        />
      </Box>
      <Box maxWidth={700}>
        <FieldSelect
          field={form.getField('category')}
          label="Category"
          options={categoryOptions}
          required
        />
      </Box>
      <Box maxWidth={700}>
        <FieldTextRequired
          field={form.getField('userFacingName')}
          label="User-Facing Label"
          helperText="This is how the tag will be visible to users in the app"
          required
        />
      </Box>
      <Divider />
      <Stack direction="row" gap={2} justifyContent="space-between">
        <Box>
          <Button variant="contained" onClick={form.handleSubmit}>
            {existing ? 'Save Changes' : 'Create Tag'}
          </Button>
        </Box>
        {existing && (
          <Stack alignItems="flex-end" gap={0.5}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setShowDeleteModal(true)}
              startIcon={<TrashSimple />}
            >
              Delete
            </Button>
            <Typography variant="body2" color="text.secondary">
              Deleting a tag will remove it from all content it is linked to
            </Typography>
          </Stack>
        )}
      </Stack>
      {showDeleteModal && (
        <ConfirmModal
          title="Delete Content Tag"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async () => {
            if (!existing) return;

            const response = await callApi(`/tags/${existing.id}`, {
              method: 'DELETE',
            });

            if (response) {
              setShowDeleteModal(false);
              toast.success('Content tag deleted');
              navigate('/library/content-tags');
            }
          }}
          color="error"
          confirmText="Delete"
        >
          Are you sure you want to delete this content tag? This action cannot
          be undone.
        </ConfirmModal>
      )}
    </Stack>
  );
}
